import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';
import { Trophy, Loader2, ShieldCheck, Info } from 'lucide-react';

const OUTCOME = {
    TP2: { label: 'TP2 tam hedef', cls: 'bg-emerald-400', chip: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' },
    TP1: { label: 'TP1 + başabaş', cls: 'bg-emerald-600', chip: 'text-emerald-200 border-emerald-600/40 bg-emerald-600/10' },
    SL: { label: 'Stop', cls: 'bg-rose-500', chip: 'text-rose-300 border-rose-500/40 bg-rose-500/10' },
    FLIPPED: { label: 'Yön değişti', cls: 'bg-amber-400', chip: 'text-amber-200 border-amber-500/40 bg-amber-500/10' },
    EXPIRED: { label: 'Süre doldu', cls: 'bg-slate-400', chip: 'text-slate-200 border-slate-500/40 bg-slate-600/20' },
};
const signed = (v, d = 2) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(d)}%`);

function useStats(binanceOnly) {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await apiClient.get('/predictions/stats', { params: { days: 30, binance_only: binanceOnly } });
                if (!cancelled) setStats(data);
            } catch (e) {
                logger.warn('stats load failed', e);
            }
        };
        load();
        const id = setInterval(load, 60000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [binanceOnly]);
    return stats;
}

export default function SystemStatsCard({ binanceOnly = false }) {
    const stats = useStats(binanceOnly);
    if (!stats) {
        return (
            <div className="border border-slate-700 bg-slate-800 rounded-md px-4 py-3 flex items-center gap-2 text-[11px] text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sistem başarı oranı yükleniyor…
            </div>
        );
    }
    return (
        <div data-testid="system-stats-card" className="border border-indigo-500/25 bg-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-700 flex items-center gap-x-3 gap-y-1 flex-wrap">
                <Trophy className="h-4 w-4 text-indigo-300" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-indigo-200 font-semibold">Sistem Başarı Oranı · Son 30 Gün</span>
                <span data-testid="stats-criteria" className="inline-flex items-center gap-1 text-[10px] text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-1.5 py-0.5">
                    <ShieldCheck className="h-3 w-3" /> Sadece RESMİ sinyaller: güven ≥%{stats.criteria?.min_confidence} · R/R ≥{stats.criteria?.min_rr}
                </span>
                {binanceOnly && <span className="text-[10px] text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded-sm">Yalnız Binance</span>}
                <span className="ml-auto text-[10px] text-slate-400" data-testid="stats-counts">
                    {stats.open_now} aktif · {stats.total_closed} kapalı
                </span>
            </div>
            {stats.total_closed === 0 ? <StatsEmpty stats={stats} /> : <StatsBody stats={stats} />}
            <HowItWorks stats={stats} />
        </div>
    );
}

const StatsEmpty = ({ stats }) => {
    const since = stats.tracking_since ? new Date(stats.tracking_since).toLocaleString('tr-TR') : '—';
    return (
        <div data-testid="stats-empty" className="px-4 py-4 text-[12px] text-slate-300 leading-relaxed">
            Yeni ve dürüst ölçüm <b className="text-slate-100">{since}</b> itibarıyla başladı. Eski veriler sıfırlandı; çoğu, saniyeler içinde yön
            değiştiren zayıf sinyallerdi ve oranı olduğundan yüksek gösteriyordu. Şu an <b className="text-slate-100">{stats.open_now}</b> resmi
            sinyal izleniyor. Her biri kapandığında (TP2 / TP1+başabaş / Stop / 24 saat / yön değişimi) gerçek oran burada görünecek.
        </div>
    );
};

const Metric = ({ label, value, sub, cls = 'text-slate-100', testid }) => (
    <div className="flex flex-col gap-0.5">
        <div className="text-[9px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className={`font-mono font-semibold text-sm ${cls}`} data-testid={testid}>{value}</div>
        {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
);

const StatsBody = ({ stats }) => {
    const wr = stats.win_rate_pct;
    return (
        <div className="px-4 py-3 space-y-4">
            <div className="flex items-end gap-6 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Gerçek Başarı</div>
                    <div data-testid="stats-win-rate" className={`text-4xl font-mono font-bold leading-none ${wr >= 55 ? 'text-emerald-300' : wr >= 45 ? 'text-amber-300' : 'text-rose-300'}`}>
                        {wr !== null ? `%${wr}` : '—'}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">
                        {stats.total_closed} işlemden <b className="text-emerald-300">{stats.wins}</b> kârlı, <b className="text-rose-300">{stats.losses}</b> zararlı
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 flex-1 min-w-[260px]">
                    <Metric label="TP1'e ulaştı" value={stats.tp1_rate_pct !== null ? `%${stats.tp1_rate_pct}` : '—'} sub={`${stats.tp1_reached} sinyal`} cls="text-emerald-300" testid="stats-tp1-rate" />
                    <Metric label="Ort. sonuç / işlem" value={signed(stats.avg_pnl_pct)} sub="kaldıraçsız" cls={stats.avg_pnl_pct >= 0 ? 'text-emerald-300' : 'text-rose-300'} testid="stats-avg-pnl" />
                    <Metric label="Kâr faktörü" value={stats.profit_factor ?? '—'} sub="kazanç ÷ kayıp (1+ iyi)" testid="stats-profit-factor" />
                    <Metric label="Ort. R/R" value={stats.avg_rr ?? '—'} testid="stats-avg-rr" />
                </div>
            </div>
            <OutcomeBar stats={stats} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ConfBuckets buckets={stats.by_confidence || []} />
                <RecentList recent={stats.recent || []} />
            </div>
        </div>
    );
};

const OutcomeBar = ({ stats }) => {
    const parts = [['TP2', stats.tp2_wins], ['TP1', stats.tp1_wins], ['SL', stats.sl_losses], ['FLIPPED', stats.flipped], ['EXPIRED', stats.expired]];
    const total = stats.total_closed || 1;
    return (
        <div data-testid="stats-outcome-bar">
            <div className="flex h-2.5 rounded-sm overflow-hidden bg-slate-700">
                {parts.map(([k, n]) => n > 0 && <div key={k} className={OUTCOME[k].cls} style={{ width: `${(n / total) * 100}%` }} title={`${OUTCOME[k].label}: ${n}`} />)}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[10px] text-slate-300">
                {parts.map(([k, n]) => (
                    <span key={k} className="inline-flex items-center gap-1" data-testid={`stats-outcome-${k}`}>
                        <span className={`h-2 w-2 rounded-sm ${OUTCOME[k].cls}`} /> {OUTCOME[k].label}: <b className="font-mono">{n}</b>
                    </span>
                ))}
            </div>
        </div>
    );
};

const ConfBuckets = ({ buckets }) => (
    <div data-testid="stats-by-confidence">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Güven aralığına göre başarı</div>
        <div className="space-y-1.5">
            {buckets.map((b) => (
                <div key={b.label} className="flex items-center gap-2 text-[11px]">
                    <span className="w-14 font-mono text-slate-300">{b.label}</span>
                    <div className="flex-1 h-1.5 bg-slate-700 rounded-sm overflow-hidden">
                        <div className="h-full bg-indigo-400" style={{ width: `${b.win_rate_pct ?? 0}%` }} />
                    </div>
                    <span className="w-24 text-right font-mono text-slate-100">
                        {b.win_rate_pct !== null ? `%${b.win_rate_pct}` : '—'} <span className="text-slate-500">({b.n})</span>
                    </span>
                </div>
            ))}
        </div>
    </div>
);

const RecentList = ({ recent }) => (
    <div data-testid="stats-recent">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Son kapanan sinyaller</div>
        <div className="flex flex-wrap gap-1.5">
            {recent.length === 0 && <span className="text-[11px] text-slate-500">—</span>}
            {recent.map((r) => (
                <span key={`${r.short_id}-${r.outcome_at}`} className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${OUTCOME[r.outcome]?.chip || ''}`}>
                    #{r.short_id} {r.symbol?.replace('USDT', '')} {r.action} · {OUTCOME[r.outcome]?.label || r.outcome} {signed(r.realized_pnl_pct)}
                </span>
            ))}
        </div>
    </div>
);

const HowItWorks = ({ stats }) => (
    <details className="border-t border-slate-700 px-4 py-2 text-[11px] text-slate-400 group" data-testid="stats-how-it-works">
        <summary className="cursor-pointer select-none flex items-center gap-1.5 text-slate-300 hover:text-slate-100">
            <Info className="h-3.5 w-3.5" /> Nasıl hesaplanıyor?
        </summary>
        <ul className="mt-2 space-y-1 list-disc pl-5 leading-relaxed">
            <li>Sadece <b>resmi</b> sinyaller sayılır: güven ≥%{stats.criteria?.min_confidence}, R/R ≥{stats.criteria?.min_rr}, en az 3 destekleyen sinyal, 4 saatlik trend ters değil.</li>
            <li>TP/Stop kontrolü 1 dakikalık mumların en yüksek ve en düşük değerleriyle yapılır, fitiller kaçmaz. Aynı mumda hem stop hem hedef görüldüyse stop sayılır (kötümser varsayım).</li>
            <li>Plan: TP1'de yarısı kapanır, stop girişe çekilir, kalan yarı TP2'ye gider. Sonuç bu plana göre kaldıraçsız % olarak hesaplanır.</li>
            <li>Yön değişimi için ters yönde resmi sinyalin <b>{stats.criteria?.flip_confirm_scans} tarama üst üste</b> gelmesi gerekir. Yön değişen ve {stats.criteria?.horizon_hours} saatte süresi dolan işlemler de o anki kâr/zararıyla orana dahildir.</li>
            <li>Başarı = kârla kapanan ÷ (kârla + zararla kapanan).</li>
        </ul>
    </details>
);
